<script setup lang="ts">
import AppGrid from "@/components/AppGrid.vue";
import { FITTABLE_DEFAULT_COL_DEF } from "@/components/composables/useGridColumnDefaults.ts";
import DashboardStateView from "@/components/dashboard/DashboardStateView.vue";
import { useRedistributionStore } from "@/stores/redistributionStore.ts";
import type { ColDef } from "ag-grid-community";
import { storeToRefs } from "pinia";
import { computed } from "vue";

const {
  beforeAfterGridRowData,
  itemMovementsGridRowData,
  originalPOs,
  updatedPOs,
} = storeToRefs(useRedistributionStore());

const hasData = computed(() => itemMovementsGridRowData.value.length > 0);

/* ── Reason label map ── */
const REASON_LABELS: Record<string, string> = {
  CriticalRisk: "Critical Stockout Prevention",
  ModerateRisk: "Moderate Stockout Prevention",
  BalanceWos: "Inventory Balancing",
  MakeRoom: "Make Room",
};

/* ── Movement Summary by Reason ── */
interface ReasonRow {
  reason: string;
  label: string;
  count: number;
  totalQty: number;
  pct: string;
  avgQty: string;
}

const reasonSummary = computed<ReasonRow[]>(() => {
  const groups: Record<string, { count: number; totalQty: number }> = {};
  let total = 0;

  for (const row of itemMovementsGridRowData.value) {
    const r = row.reason ?? "Unknown";
    groups[r] ??= { count: 0, totalQty: 0 };
    groups[r].count++;
    groups[r].totalQty += row.qty;
    total++;
  }

  return Object.entries(groups)
    .toSorted((a, b) => b[1].count - a[1].count)
    .map(([reason, g]) => ({
      reason,
      label: REASON_LABELS[reason] ?? reason,
      count: g.count,
      totalQty: g.totalQty,
      pct: total > 0 ? `${((g.count / total) * 100).toFixed(1)}%` : "—",
      avgQty: g.count > 0 ? (g.totalQty / g.count).toFixed(1) : "—",
    }));
});

const reasonTotal = computed(() => {
  let count = 0;
  let totalQty = 0;

  for (const row of reasonSummary.value) {
    count += row.count;
    totalQty += row.totalQty;
  }

  return {
    count,
    totalQty,
    avgQty: count > 0 ? (totalQty / count).toFixed(1) : "—",
  };
});

/* ── WOS Impact Summary ── */
const wosImpact = computed(() => {
  let beforeCount = 0;
  let beforeSum = 0;
  let afterCount = 0;
  let afterSum = 0;
  let totalUnits = 0;

  for (const row of itemMovementsGridRowData.value) {
    totalUnits += row.qty;

    if (row.wosBefore != null) {
      beforeCount++;
      beforeSum += row.wosBefore;
    }

    if (row.wosAfter != null) {
      afterCount++;
      afterSum += row.wosAfter;
    }
  }

  const avgBefore = beforeCount > 0 ? beforeSum / beforeCount : null;
  const avgAfter = afterCount > 0 ? afterSum / afterCount : null;
  const improvement =
    avgBefore != null && avgAfter != null ? avgAfter - avgBefore : null;

  return {
    avgBefore: avgBefore == null ? "—" : avgBefore.toFixed(1),
    avgAfter: avgAfter == null ? "—" : avgAfter.toFixed(1),
    improvement:
      improvement == null
        ? "—"
        : `${improvement >= 0 ? "+" : ""}${improvement.toFixed(1)}w`,
    totalUnits,
  };
});

/* ── Top 10 SKUs by Units Moved ── */
const top10ByUnits = computed(() =>
  itemMovementsGridRowData.value
    .toSorted((a, b) => b.qty - a.qty)
    .slice(0, 10)
    .map((r, i) => ({ ...r, rank: i + 1 }))
);

/* ── PO Impact Summary ── */
interface PoImpactRow {
  po: string;
  skuCount: number;
  qtyBefore: number;
  qtyAfter: number;
  change: number;
}

const poImpact = computed<PoImpactRow[]>(() => {
  const poNumbers = [
    ...new Set([
      ...originalPOs.value.map((p) => p.purchaseOrderNumber),
      ...updatedPOs.value.map((p) => p.purchaseOrderNumber),
    ]),
  ];
  return poNumbers.map((poNum) => {
    const orig = originalPOs.value.find((p) => p.purchaseOrderNumber === poNum);
    const updated = updatedPOs.value.find(
      (p) => p.purchaseOrderNumber === poNum
    );

    let qtyBefore = 0;
    for (const sku of orig?.skus ?? []) {
      qtyBefore += sku.lineItemQuantity;
    }

    let qtyAfter = 0;
    for (const sku of updated?.skus ?? []) {
      qtyAfter += sku.lineItemQuantity;
    }

    const skuCount = updated?.skus.length ?? orig?.skus.length ?? 0;
    return {
      po: poNum,
      skuCount,
      qtyBefore,
      qtyAfter,
      change: qtyAfter - qtyBefore,
    };
  });
});

/* ── Reason badge class helper ── */
function reasonBadgeClass(reason: string): string {
  switch (reason) {
    case "CriticalRisk": {
      return "dashboard-status-badge dashboard-status-badge--critical";
    }
    case "ModerateRisk": {
      return "dashboard-status-badge dashboard-status-badge--moderate";
    }
    case "BalanceWos": {
      return "dashboard-status-badge dashboard-status-badge--balanced";
    }
    default: {
      return "dashboard-status-badge dashboard-status-badge--healthy";
    }
  }
}

/* ── Cell Renderer: Reason Badge ── */
function renderReasonBadge(params: { value: string }): HTMLElement {
  const reason = params.value || "";
  const label = REASON_LABELS[reason] ?? reason;
  const span = document.createElement("span");
  span.className = reasonBadgeClass(reason);
  span.textContent = label;
  return span;
}

function formatNumberValue(value: unknown): string {
  if (typeof value === "number") {
    return value.toLocaleString();
  }
  if (typeof value === "string") {
    return value;
  }
  return "—";
}

type ReasonGridRow = ReasonRow & { isTotal?: boolean };

function totalRowClass(params: { data: ReasonGridRow | undefined }): string {
  return params.data?.isTotal ? "font-bold border-t-2" : "";
}

/* ── Column Definitions for Movement Summary ── */
const movementSummaryColumnDefs: ColDef[] = [
  {
    field: "label",
    headerName: "Reason",
    sortable: true,
    filter: "agTextColumnFilter",
    filterParams: {
      maxNumConditions: 5,
    },
    flex: 2,
    minWidth: 120,
    cellClass: totalRowClass,
  },
  {
    field: "count",
    headerName: "Count",
    sortable: true,
    filter: "agNumberColumnFilter",
    filterParams: {
      maxNumConditions: 5,
    },
    type: "numericColumn",
    flex: 0.7,
    minWidth: 65,
    cellClass: totalRowClass,
  },
  {
    field: "pct",
    headerName: "% of Total",
    sortable: true,
    filter: false,
    flex: 0.7,
    minWidth: 65,
    cellClass: totalRowClass,
  },
  {
    field: "avgQty",
    headerName: "Avg Units Moved",
    sortable: true,
    filter: false,
    flex: 0.9,
    minWidth: 72,
    cellClass: totalRowClass,
  },
  {
    field: "totalQty",
    headerName: "Total Units",
    sortable: true,
    filter: "agNumberColumnFilter",
    filterParams: {
      maxNumConditions: 5,
    },
    type: "numericColumn",
    valueFormatter: (params: { value: unknown }) =>
      formatNumberValue(params.value),
    flex: 0.8,
    minWidth: 72,
    cellClass: totalRowClass,
  },
];

const movementSummaryRowData = computed(() => [
  ...reasonSummary.value.map((row) => ({
    ...row,
  })),
  {
    reason: "Total",
    label: "Total",
    count: reasonTotal.value.count,
    totalQty: reasonTotal.value.totalQty,
    pct: "100%",
    avgQty: reasonTotal.value.avgQty,
    isTotal: true,
  },
]);

/* ── Column Definitions for Top 10 SKUs ── */
const top10ColumnDefs: ColDef[] = [
  {
    field: "rank",
    headerName: "#",
    sortable: true,
    filter: false,
    flex: 0.4,
    minWidth: 44,
    suppressSizeToFit: true,
  },
  {
    field: "sku",
    headerName: "SKU",
    sortable: true,
    filter: "agTextColumnFilter",
    filterParams: {
      maxNumConditions: 5,
    },
    flex: 0.8,
    minWidth: 70,
  },
  {
    field: "description",
    headerName: "Description",
    sortable: true,
    filter: "agTextColumnFilter",
    filterParams: {
      maxNumConditions: 5,
    },
    flex: 2,
    minWidth: 120,
  },
  {
    field: "qty",
    headerName: "Qty Moved",
    sortable: true,
    filter: "agNumberColumnFilter",
    filterParams: {
      maxNumConditions: 5,
    },
    type: "numericColumn",
    valueFormatter: (params: { value: unknown }) =>
      formatNumberValue(params.value),
    flex: 0.7,
    minWidth: 65,
  },
  {
    field: "from",
    headerName: "From PO",
    sortable: true,
    filter: "agTextColumnFilter",
    filterParams: {
      maxNumConditions: 5,
    },
    flex: 0.8,
    minWidth: 70,
  },
  {
    field: "to",
    headerName: "To PO",
    sortable: true,
    filter: "agTextColumnFilter",
    filterParams: {
      maxNumConditions: 5,
    },
    flex: 0.8,
    minWidth: 70,
  },
  {
    field: "reason",
    headerName: "Reason",
    sortable: true,
    filter: "agTextColumnFilter",
    filterParams: {
      maxNumConditions: 5,
    },
    cellRenderer: renderReasonBadge,
    flex: 1.2,
    minWidth: 100,
  },
];

const top10RowData = computed(() => top10ByUnits.value);

/* ── Column Definitions for PO Impact ── */
const poImpactColumnDefs: ColDef[] = [
  {
    field: "po",
    headerName: "PO",
    sortable: true,
    filter: "agTextColumnFilter",
    filterParams: {
      maxNumConditions: 5,
    },
    flex: 1,
    minWidth: 80,
  },
  {
    field: "skuCount",
    headerName: "SKUs",
    sortable: true,
    filter: "agNumberColumnFilter",
    filterParams: {
      maxNumConditions: 5,
    },
    type: "numericColumn",
    flex: 0.6,
    minWidth: 60,
  },
  {
    field: "qtyBefore",
    headerName: "Qty Before",
    sortable: true,
    filter: "agNumberColumnFilter",
    filterParams: {
      maxNumConditions: 5,
    },
    type: "numericColumn",
    valueFormatter: (params: { value: unknown }) =>
      formatNumberValue(params.value),
    flex: 0.8,
    minWidth: 72,
  },
  {
    field: "qtyAfter",
    headerName: "Qty After",
    sortable: true,
    filter: "agNumberColumnFilter",
    filterParams: {
      maxNumConditions: 5,
    },
    type: "numericColumn",
    valueFormatter: (params: { value: unknown }) =>
      formatNumberValue(params.value),
    flex: 0.8,
    minWidth: 72,
  },
  {
    field: "change",
    headerName: "Change",
    sortable: true,
    filter: "agNumberColumnFilter",
    filterParams: {
      maxNumConditions: 5,
    },
    type: "numericColumn",
    cellRenderer: (params: { value: number }) => {
      if (params.value === 0) return "—";
      const className = params.value > 0 ? "text-green-500" : "text-red-500";
      const display =
        params.value > 0 ? `+${String(params.value)}` : String(params.value);
      return `<span class="${className} font-semibold">${display}</span>`;
    },
    flex: 0.7,
    minWidth: 65,
  },
];

const poImpactRowData = computed(() => poImpact.value);

/* ── AG Grid Default Column Def ── */
const gridDefaultColDef: ColDef = FITTABLE_DEFAULT_COL_DEF;

/* ── Unused import guard ── */
void beforeAfterGridRowData;
</script>

<template>
  <div class="dashboard-page page-main-container gap-5">
    <!-- Page title and subtitle live in AppHeader (route meta). -->

    <!-- Empty state -->
    <DashboardStateView
      v-if="!hasData"
      tone="empty"
      title="No report data available"
      message="Run a redistribution to generate operational reports. Select POs from the PO Recommendations workflow and submit."
    />

    <template v-else>
      <!-- 1. Movement Summary by Reason -->
      <section
        class="dashboard-card min-w-0 overflow-hidden rounded-[1.5rem] px-5 py-5"
      >
        <h2
          class="dashboard-divider dashboard-text-muted mb-4 border-b pb-3 text-xs font-semibold tracking-[0.14em] uppercase"
        >
          Movement Summary by Reason
        </h2>
        <AppGrid
          id="movement-summary-grid"
          :column-defs="movementSummaryColumnDefs"
          :row-data="movementSummaryRowData"
          :default-col-def="gridDefaultColDef"
          :dom-layout="'autoHeight'"
          :suppress-column-movement="true"
          :enable-column-menu="false"
        />
      </section>

      <!-- 2. WOS Impact Summary -->
      <section
        class="dashboard-card min-w-0 overflow-hidden rounded-[1.5rem] px-5 py-5"
      >
        <h2
          class="dashboard-divider dashboard-text-muted mb-4 border-b pb-3 text-xs font-semibold tracking-[0.14em] uppercase"
        >
          WOS Impact Summary
        </h2>
        <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div>
            <p
              class="dashboard-text-muted text-xs font-semibold tracking-[0.12em] uppercase"
            >
              Avg WOS Before
            </p>
            <p class="dashboard-text-primary mt-1 text-2xl font-bold">
              {{ wosImpact.avgBefore
              }}<span
                v-if="wosImpact.avgBefore !== '—'"
                class="dashboard-text-secondary text-sm font-normal"
                >w</span
              >
            </p>
          </div>
          <div>
            <p
              class="dashboard-text-muted text-xs font-semibold tracking-[0.12em] uppercase"
            >
              Avg WOS After
            </p>
            <p class="dashboard-text-primary mt-1 text-2xl font-bold">
              {{ wosImpact.avgAfter
              }}<span
                v-if="wosImpact.avgAfter !== '—'"
                class="dashboard-text-secondary text-sm font-normal"
                >w</span
              >
            </p>
          </div>
          <div>
            <p
              class="dashboard-text-muted text-xs font-semibold tracking-[0.12em] uppercase"
            >
              Avg WOS Improvement
            </p>
            <p
              class="mt-1 text-2xl font-bold"
              :class="
                wosImpact.improvement.startsWith('+')
                  ? 'text-green-500'
                  : wosImpact.improvement === '—'
                    ? 'dashboard-text-primary'
                    : 'text-red-500'
              "
            >
              {{ wosImpact.improvement }}
            </p>
          </div>
          <div>
            <p
              class="dashboard-text-muted text-xs font-semibold tracking-[0.12em] uppercase"
            >
              Total Units Redistributed
            </p>
            <p class="dashboard-text-primary mt-1 text-2xl font-bold">
              {{ wosImpact.totalUnits.toLocaleString() }}
            </p>
          </div>
        </div>
      </section>

      <!-- 3. Top 10 SKUs by Units Moved -->
      <section
        class="dashboard-card min-w-0 overflow-hidden rounded-[1.5rem] px-5 py-5"
      >
        <h2
          class="dashboard-divider dashboard-text-muted mb-4 border-b pb-3 text-xs font-semibold tracking-[0.14em] uppercase"
        >
          Top 10 SKUs by Units Moved
        </h2>
        <AppGrid
          id="top-10-skus-grid"
          :column-defs="top10ColumnDefs"
          :row-data="top10RowData"
          :default-col-def="gridDefaultColDef"
          :dom-layout="'autoHeight'"
          :suppress-column-movement="true"
          :enable-column-menu="false"
        />
      </section>

      <!-- 4. PO Impact Summary -->
      <section
        class="dashboard-card min-w-0 overflow-hidden rounded-[1.5rem] px-5 py-5"
      >
        <h2
          class="dashboard-divider dashboard-text-muted mb-4 border-b pb-3 text-xs font-semibold tracking-[0.14em] uppercase"
        >
          PO Impact Summary
        </h2>
        <AppGrid
          id="po-impact-grid"
          :column-defs="poImpactColumnDefs"
          :row-data="poImpactRowData"
          :default-col-def="gridDefaultColDef"
          :dom-layout="'autoHeight'"
          :suppress-column-movement="true"
          :enable-column-menu="false"
        />
      </section>
    </template>
  </div>
</template>
